"use client";

import { useState, useCallback } from "react";
import { CalculatorIcon } from "lucide-react";
import { Section } from "@marketing/shared/components/bento/Bento";
import { CollapsedTakeaways } from "@marketing/shared/components/bento/KeyTakeaways";
import { DEFAULT_MODELS, type ModelId } from "../lib/statistics";
import { ConfidenceControls } from "./ConfidenceControls";
import { StatCards } from "./StatCards";
import { ConfidenceChart } from "./ConfidenceChart";
import { HeatmapTable } from "./HeatmapTable";
import { ManualEffort } from "./ManualEffort";
import { InterpretationPanel } from "./InterpretationPanel";

export function CalculatorClient() {
  const [conf, setConf] = useState(0.9);
  const [rate, setRate] = useState(50);
  const [prompts, setPrompts] = useState(10);
  const [selectedModels, setSelectedModels] = useState<Set<ModelId>>(
    () => new Set(DEFAULT_MODELS),
  );

  const p = rate / 100;

  const handleToggleModel = useCallback((id: ModelId) => {
    setSelectedModels((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size === 1) return prev;
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return (
    <>
      {/* Hero */}
      <Section className="pt-24 lg:pt-28">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border bg-muted px-3 py-1 text-sm font-medium text-muted-foreground">
            <CalculatorIcon className="size-4" />
            <span>Free Statistical Confidence Tool</span>
          </div>

          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
            Is Your AI Visibility{" "}
            <span className="text-primary">Data Reliable?</span>
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            AI responses are nondeterministic. A single run tells you almost
            nothing. Explore how prompt volume, run frequency, and model coverage
            affect the statistical confidence of your visibility data, and see the
            hidden cost of tracking this manually.
          </p>
        </div>
      </Section>

      {/* Entity-dense primary descriptive section + key takeaways (AEO) */}
      <Section muted>
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
            Why one AI run tells you almost nothing
          </h2>
          <div className="mt-4 space-y-3 text-lg text-muted-foreground">
            <p>
              Large language models are nondeterministic: ask ChatGPT, Gemini, or
              Claude the same question twice and the brands it recommends can change.
              Measuring AI visibility from a single run — or a handful of prompts —
              produces a number with a confidence interval wide enough to be
              meaningless. This calculator models how prompt volume, run frequency,
              and model coverage tighten that interval, using the binomial statistics
              behind a reliable share-of-voice estimate.
            </p>
            <p>
              The math is unforgiving at small samples. Detecting a real shift in
              mention rate — the Minimum Detectable Change — requires enough
              independent observations to separate signal from run-to-run noise. The
              tool shows the sample size needed for a given confidence level, and the
              hidden hours of manual prompting that a scheduled, multi-model tracker
              replaces.
            </p>
          </div>
          <CollapsedTakeaways
            className="mt-6"
            summary="Key takeaways"
            facts={[
              "AI responses are nondeterministic — a single run's share of voice carries a confidence interval too wide to act on.",
              "Reliability scales with prompt volume × run frequency × model coverage, not with any one of them alone.",
              "The Minimum Detectable Change sets how big a shift you can even see at a given sample size — small samples hide real movement.",
              "Confidence comes from independent observations; a scheduled tracker across ChatGPT, Gemini, and Claude reaches it without manual prompting.",
            ]}
          />
        </div>
      </Section>

      {/* Calculator */}
      <Section>
        <div className="mx-auto max-w-5xl space-y-6">
          <ConfidenceControls
            conf={conf}
            onConfChange={setConf}
            rate={rate}
            onRateChange={setRate}
            prompts={prompts}
            onPromptsChange={setPrompts}
            selectedModels={selectedModels}
            onToggleModel={handleToggleModel}
          />

          <InterpretationPanel
            prompts={prompts}
            selectedModels={selectedModels}
            p={p}
            conf={conf}
          />

          <StatCards prompts={prompts} p={p} conf={conf} />

          <ConfidenceChart prompts={prompts} p={p} conf={conf} mode="ci" />
          <ConfidenceChart prompts={prompts} p={p} conf={conf} mode="mdc" />

          <HeatmapTable p={p} conf={conf} />

          <ManualEffort
            prompts={prompts}
            selectedModels={selectedModels}
            p={p}
            conf={conf}
          />
        </div>
      </Section>
    </>
  );
}
